<template>
  <div class="p-6 max-w-[1600px] mx-auto space-y-8">
    <!-- Header & Skeletons -->
    <template v-if="!investment">
      <div class="space-y-4">
        <Skeleton class="h-10 w-1/3" />
        <Skeleton class="h-4 w-1/2" />
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Skeleton class="lg:col-span-2 h-[450px]" />
        <Skeleton class="h-[450px]" />
      </div>
    </template>

    <template v-else>
      <InvestmentHeader 
        :investment="investment" 
        @back="router.push({ name: 'finance_home' })"
        @edit="router.push({ name: 'finance_investment_edit', params: { id: investment.id } })"
      />

      <!-- Dashboard grid: chart + stats -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Chart card -->
        <EvolutionChart
          v-model:displayMode="displayMode"
          :chart-data="chartData"
          :chart-options="chartOptions"
          :chart-key="chartKey"
          :is-chart-fullscreen="isChartFullscreen"
          :category-sums="categorySums"
          :is-amounts-hidden="isAmountsHidden"
          :is-refreshing="loadingMovements || loadingSimulation"
          @toggle-fullscreen="toggleFullscreen"
        />

        <!-- Stats card -->
        <ProfitabilityCards
          :invested-amount="investmentInvestedAmount"
          :current-value="investmentCurrentValue"
          :annualized-rate="annualizedRate"
          :is-amounts-hidden="isAmountsHidden"
          @add-movement="openMovementModal(null)"
          @update-valuation="showValuationModal = true"
          @import-csv="$refs.csvFileInput.click()"
          @clear-history="showClearHistoryModal = true"
        />
        <input type="file" ref="csvFileInput" accept=".csv" @change="handleFileUpload" class="hidden" />
      </div>

      <!-- Filters -->
      <InvestmentFilters 
        v-if="movements.length > 0"
        v-model:selectedCategories="selectedFilterCategories"
        v-model:selectedYear="selectedFilterYear"
        :movement-categories="movementCategories"
        :available-years="availableYears"
      />

      <!-- Movements Tables -->
      <InvestmentMovements 
        :realized-groups="realizedGroups"
        :future-groups="futureGroups"
        :is-amounts-hidden="isAmountsHidden"
        @edit="openMovementModal"
        @duplicate="duplicateMovement"
        @delete="confirmDelete"
        @delete-import="confirmDeleteImport"
        @validate="validateMovement"
      />
    </template>

    <!-- ======================== MODALS ======================== -->

    <!-- Deconstructed inline modals -->
    <InvestmentModals 
        v-model:showDeleteModal="showDeleteModal"
        v-model:showCategoryModal="showCategoryModal"
        v-model:showClearHistoryModal="showClearHistoryModal"
        v-model:showDeleteImportModal="showDeleteImportModal"
        :movement-to-delete="movementToDelete"
        :movement-categories="movementCategories"
        :creating-cat="creatingCat"
        :clearing-history="clearingHistory"
        :investment-name="investment?.name ?? ''"
        @perform-delete="performDelete"
        @create-category="createCategory"
        @clear-history="handleClearHistory"
        @delete-import="handleDeleteImport"
    />

    <!-- Add/Edit movement modal -->
    <MovementModal
      v-if="showMovementModal"
      :show="showMovementModal"
      :is-editing="isEditingMovement"
      :has-group="currentEditingHasGroup"
      :saving="savingMovement"
      :initial-form="formM"
      :movement-categories="movementCategories"
      :investments="allInvestments"
      :current-investment-id="investmentId"
      @close="showMovementModal = false"
      @submit="handleMovementSubmit"
      @open-category-modal="showCategoryModal = true"
    />

    <!-- CSV Import modal -->
    <CsvImportModal
      v-if="showImportModal"
      :show="showImportModal"
      :config="importConfig"
      :row-count="importData.length"
      :investment-name="investment?.name ?? 'Placement'"
      :importing="importingFile"
      @close="showImportModal = false"
      @import="processImport($event, investment?.name ?? 'Placement')"
    />

    <!-- Valuation modal -->
    <ValuationModal
      v-if="showValuationModal"
      :show="showValuationModal"
      :investment="investment"
      :movementCategories="movementCategories"
      :rawMovementCategories="rawMovementCategories"
      @close="showValuationModal = false"
      @saved="onValuationSaved"
    />

    <ToastNotification />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// UI Components
import { Skeleton } from '@/components/ui/skeleton'

// Composables
import { useToast } from '../../composables/useToast.js'
import { useMovements } from '../../composables/useMovements.js'
import { useInvestmentChart } from '../../composables/useInvestmentChart.js'
import { useGlobalState } from '../../composables/useGlobalState.js'
import { useCsvImport } from '../../composables/useCsvImport.js'
import { slugify } from '@/lib/utils'

// Sub-Components
import ToastNotification from './ToastNotification.vue'
import InvestmentHeader from './InvestmentDetail/InvestmentHeader.vue'
import ProfitabilityCards from './InvestmentDetail/ProfitabilityCards.vue'
import EvolutionChart from './InvestmentDetail/EvolutionChart.vue'
import InvestmentFilters from './InvestmentDetail/InvestmentFilters.vue'
import InvestmentMovements from './InvestmentDetail/InvestmentMovements.vue'
import InvestmentModals from './InvestmentDetail/InvestmentModals.vue'

// Lazy loaded modals
const MovementModal = defineAsyncComponent(() => import('./Modals/MovementModal.vue'))
const CsvImportModal = defineAsyncComponent(() => import('./Modals/CsvImportModal.vue'))
const ValuationModal = defineAsyncComponent(() => import('./Modals/ValuationModal.vue'))


// =============================================
// CORE SETUP
// =============================================
const route = useRoute()
const router = useRouter()
const { isAmountsHidden, getAuthHeader } = useGlobalState()
const investmentId = route.params.id
const toast = useToast()

// =============================================
// INVESTMENT DATA
// =============================================
const investment = ref(null)
const allInvestments = ref([])

const fetchInvestment = async () => {
  try {
    const res = await fetch(`/api/investments/${investmentId}`, { headers: getAuthHeader() })
    if (res.ok) {
        investment.value = await res.json()
        
        // Canonical redirect if name mismatch
        const currentSlug = route.params.name || ''
        const expectedSlug = slugify(investment.value.name)
        if (currentSlug !== expectedSlug) {
            router.replace({ 
                name: 'finance_investment_detail', 
                params: { id: investmentId, name: expectedSlug } 
            })
        }
    }
  } catch (e) { console.error(e) }
}

const fetchAllInvestments = async () => {
  try {
    const res = await fetch('/api/investments', { headers: getAuthHeader() })
    if (res.ok) {
        allInvestments.value = await res.json()
    }
  } catch (e) { console.error(e) }
}

// =============================================
// MOVEMENTS COMPOSABLE
// =============================================
const {
  movements, investmentCurrentValue, investmentInvestedAmount,
  loadingMovements, loadingSimulation,
  fetchMovements, loadFromCache, validateMovement, deleteMovement, clearHistory, deleteImportBatch,
  selectedFilterCategories, selectedFilterYear, availableYears,
  realizedGroups, futureGroups, categorySums, rawRealizedMovements,
} = useMovements(investmentId, getAuthHeader, investment)

// =============================================
// CATEGORIES
// =============================================
const rawMovementCategories = ref([])

const movementCategories = computed(() => {
  const buildTree = (list, parentId = null, depth = 0) => {
    let tree = []
    list.filter(c => c.parentId === parentId).forEach(c => {
      tree.push({ ...c, displayName: '\u00A0\u00A0\u00A0'.repeat(depth) + (depth > 0 ? '└ ' : '') + c.name })
      tree = tree.concat(buildTree(list, c.id, depth + 1))
    })
    return tree
  }
  return buildTree(rawMovementCategories.value)
})

const fetchCategories = async () => {
  try {
    const res = await fetch('/api/movements/categories', { headers: getAuthHeader() })
    if (res.ok) rawMovementCategories.value = await res.json()
  } catch (e) { console.error(e) }
}

// Category Creation
const showCategoryModal = ref(false)
const creatingCat = ref(false)

const createCategory = async (data) => {
  creatingCat.value = true
  try {
    const res = await fetch('/api/movements/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(data)
    })
    if (res.ok) {
      const newCat = await res.json()
      rawMovementCategories.value.push(newCat)
      showCategoryModal.value = false
      // Auto-select in form if not compound
      if (!formM.value.isCompound) formM.value.categoryId = newCat.id
    }
  } catch (e) { console.error(e) }
  finally { creatingCat.value = false }
}

// =============================================
// CHART COMPOSABLE
// =============================================
const { isChartFullscreen, displayMode, chartKey, toggleFullscreen, annualizedRate, chartData, chartOptions } =
  useInvestmentChart(rawRealizedMovements, investmentCurrentValue)

// =============================================
// MOVEMENT MODAL (Add / Edit)
// =============================================
const showMovementModal = ref(false)
const savingMovement = ref(false)
const isEditingMovement = ref(false)
const currentEditingId = ref(null)
const currentEditingHasGroup = ref(false)

const defaultForm = () => ({
  type: 'payment', amount: '', date: new Date().toISOString().split('T')[0],
  label: '', categoryId: null, isRealized: true,
  recurrenceType: 'none', recurrenceCount: null, recurrenceEnd: '',
  updateGroup: false, isCompound: false,
  impactsCurrentValue: true, impactsInvestedAmount: true, subMovements: [],
  transferInvestmentId: null
})

const formM = ref(defaultForm())

const openMovementModal = (movement = null) => {
  if (movement) {
    isEditingMovement.value = true
    currentEditingId.value = movement.id
    currentEditingHasGroup.value = !!movement.recurrenceGroupId
    const hasSubs = movement.subMovements && movement.subMovements.length > 0
    formM.value = {
      type: movement.type, label: movement.label || '',
      amount: hasSubs ? '' : movement.amount,
      date: movement.date,
      categoryId: hasSubs ? null : (movement.category ? movement.category.id : null),
      isRealized: movement.isRealized, recurrenceType: 'none', updateGroup: false,
      isCompound: hasSubs,
      impactsCurrentValue: movement.impactsCurrentValue,
      impactsInvestedAmount: movement.impactsInvestedAmount,
      transferInvestmentId: movement.transferInvestmentId,
      subMovements: hasSubs ? JSON.parse(JSON.stringify(movement.subMovements)).map(s => ({
        amount: s.amount, categoryId: s.category ? s.category.id : null, type: s.type,
        impactsCurrentValue: !!s.impactsCurrentValue, impactsInvestedAmount: !!s.impactsInvestedAmount
      })) : []
    }
  } else {
    isEditingMovement.value = false
    currentEditingId.value = null
    currentEditingHasGroup.value = false
    formM.value = defaultForm()
  }
  showMovementModal.value = true
}

const duplicateMovement = (movement) => {
  isEditingMovement.value = false
  currentEditingId.value = null
  currentEditingHasGroup.value = false
  const hasSubs = movement.subMovements && movement.subMovements.length > 0
  formM.value = {
    type: movement.type, label: movement.label || '',
    amount: hasSubs ? '' : movement.amount, date: movement.date,
    categoryId: hasSubs ? null : (movement.category ? movement.category.id : null),
    isRealized: movement.isRealized, recurrenceType: 'none', updateGroup: false,
    isCompound: hasSubs, impactsCurrentValue: !!movement.impactsCurrentValue,
    impactsInvestedAmount: !!movement.impactsInvestedAmount,
    transferInvestmentId: movement.transferInvestmentId,
    subMovements: hasSubs ? JSON.parse(JSON.stringify(movement.subMovements)).map(s => ({
      amount: s.amount, categoryId: s.category ? s.category.id : null, type: s.type,
      impactsCurrentValue: !!s.impactsCurrentValue, impactsInvestedAmount: !!s.impactsInvestedAmount
    })) : []
  }
  showMovementModal.value = true
}

const handleMovementSubmit = async (data) => {
  savingMovement.value = true
  try {
    const url = isEditingMovement.value ? `/api/movements/${currentEditingId.value}` : '/api/movements'
    const method = isEditingMovement.value ? 'PUT' : 'POST'
    const payload = { ...data, investmentId }

    if (payload.isCompound) {
      payload.amount = data._compoundTotal
      payload.categoryId = null
      payload.impactsCurrentValue = false
      payload.impactsInvestedAmount = false
    } else {
      payload.subMovements = []
    }
    if (payload.recurrenceType === 'none') {
      payload.recurrenceCount = null
      payload.recurrenceEnd = null
    }
    delete payload._compoundTotal

    const editingMovement = movements.value.find(m => m.id === currentEditingId.value)
    const isImportSeries = editingMovement?.recurrenceGroupId?.startsWith('import_')
    const splitGroupUpdate = isEditingMovement.value && payload.updateGroup && isImportSeries

    if (splitGroupUpdate) {
      const res1 = await fetch(url, {
        method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, updateGroup: false })
      })
      if (!res1.ok) { toast.error((await res1.json()).error || 'Erreur lors de la sauvegarde.'); return }
      await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ label: payload.label ?? null, updateGroup: true }) })
    } else {
      const res = await fetch(url, { method, headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      if (!res.ok) { toast.error((await res.json()).error || 'Erreur lors de la sauvegarde.'); return }
    }

    showMovementModal.value = false
    fetchMovements()
    toast.success('Mouvement enregistré avec succès.')
    window.dispatchEvent(new Event('refreshSidebar'))
  } catch (e) {
    toast.error('Erreur réseau.')
  } finally {
    savingMovement.value = false
  }
}

// =============================================
// DELETE MOVEMENT
// =============================================
const showDeleteModal = ref(false)
const movementToDelete = ref(null)

const confirmDelete = (movement) => { movementToDelete.value = movement; showDeleteModal.value = true }

const performDelete = async (deleteGroup) => {
  await deleteMovement(movementToDelete.value.id, deleteGroup)
  showDeleteModal.value = false
  movementToDelete.value = null
}

// =============================================
// CLEAR HISTORY
// =============================================
const showClearHistoryModal = ref(false)
const clearingHistory = ref(false)

const handleClearHistory = async () => {
  clearingHistory.value = true
  const ok = await clearHistory(investment.value.id)
  if (ok) { showClearHistoryModal.value = false }
  clearingHistory.value = false
}

// =============================================
// CSV IMPORT COMPOSABLE
// =============================================
const {
    showImportModal, importingFile, importData, importConfig,
    handleFileUpload, processImport
} = useCsvImport(investmentId, getAuthHeader, rawMovementCategories, fetchMovements)

// =============================================
// DELETE IMPORT BATCH
// =============================================
const showDeleteImportModal = ref(false)
const importToDelete = ref(null)

const confirmDeleteImport = (importId) => { importToDelete.value = importId; showDeleteImportModal.value = true }

const handleDeleteImport = async () => {
  await deleteImportBatch(importToDelete.value)
  showDeleteImportModal.value = false
  importToDelete.value = null
}

// =============================================
// VALUATION MODAL
// =============================================
const showValuationModal = ref(false)

const onValuationSaved = () => {
  showValuationModal.value = false
  fetchMovements()
  fetchInvestment()
}

// =============================================
// LIFECYCLE
// =============================================
onMounted(() => {
  loadFromCache()
  fetchInvestment()
  fetchMovements()
  fetchCategories()
  fetchAllInvestments()
})
</script>

<style>
/* Multiselect customization to match Zinc theme */
.custom-multiselect .multiselect__tags {
  @apply bg-background border-input ring-offset-background;
}
.custom-multiselect .multiselect__single, 
.custom-multiselect .multiselect__input {
  @apply bg-background text-foreground text-sm;
}
.custom-multiselect .multiselect__option--highlight {
  @apply bg-primary text-primary-foreground;
}
.custom-multiselect .multiselect__option--selected {
  @apply bg-muted text-muted-foreground font-bold;
}
.custom-multiselect .multiselect__content-wrapper {
  @apply bg-popover border-border border rounded-md shadow-md;
}
</style>
